import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ZoomIn, RotateCcw, RotateCw, Crop, ScanLine, Undo2, Loader2 } from 'lucide-react';
import { detectAndWarp } from '@/lib/docEdgeDetect';

const loadImage = (src) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => res(img);
  img.onerror = rej;
  img.src = src;
});

// Render the (optionally rotated) source onto a canvas and crop to the selected
// pixel box. Output is a JPEG blob, capped at 2200px on the long side so document
// text stays legible without producing oversized files.
export async function getCroppedBlob(src, cropPixels, rotation = 0) {
  const image = await loadImage(src);
  const rot = (rotation * Math.PI) / 180;
  const bW = Math.abs(Math.cos(rot) * image.width) + Math.abs(Math.sin(rot) * image.height);
  const bH = Math.abs(Math.sin(rot) * image.width) + Math.abs(Math.cos(rot) * image.height);
  const stage = document.createElement('canvas');
  stage.width = bW; stage.height = bH;
  const sctx = stage.getContext('2d');
  sctx.fillStyle = '#fff'; sctx.fillRect(0, 0, bW, bH);
  sctx.translate(bW / 2, bH / 2);
  sctx.rotate(rot);
  sctx.drawImage(image, -image.width / 2, -image.height / 2);

  const cw = Math.round(cropPixels.width), ch = Math.round(cropPixels.height);
  const MAX = 2200;
  const scale = Math.max(cw, ch) > MAX ? MAX / Math.max(cw, ch) : 1;
  const out = document.createElement('canvas');
  out.width = Math.round(cw * scale); out.height = Math.round(ch * scale);
  const octx = out.getContext('2d');
  octx.fillStyle = '#fff'; octx.fillRect(0, 0, out.width, out.height);
  octx.drawImage(stage, cropPixels.x, cropPixels.y, cw, ch, 0, 0, out.width, out.height);
  return new Promise((res) => out.toBlob((b) => res(b), 'image/jpeg', 0.92));
}

const ASPECTS = [
  { key: 'card', label: 'ID card', value: 85.6 / 53.98 },
  { key: 'portrait', label: 'Portrait', value: 53.98 / 85.6 },
  { key: 'document', label: 'Document', value: 8.5 / 11 },
  { key: 'square', label: 'Square', value: 1 },
];

export const DocumentCropDialog = ({ src, title = 'Crop & adjust document', busy = false, onConfirm, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(ASPECTS[0].value);
  const [pixels, setPixels] = useState(null);
  const [workSrc, setWorkSrc] = useState(src);
  const [detecting, setDetecting] = useState(false);
  const [detectMsg, setDetectMsg] = useState('');
  const [detected, setDetected] = useState(false);
  const onComplete = useCallback((_a, px) => setPixels(px), []);

  const runDetect = useCallback(async (auto = false) => {
    setDetecting(true); setDetectMsg('');
    try {
      const res = await detectAndWarp(src);
      if (res) {
        setWorkSrc(res.dataUrl);
        setAspect(res.width / res.height);
        setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
        setDetected(true);
        setDetectMsg('Edges detected — fine-tune if needed.');
      } else {
        setDetectMsg(auto ? '' : 'No card edges found — adjust manually.');
      }
    } catch (e) {
      setDetectMsg(auto ? '' : 'Auto-detect unavailable — adjust manually.');
    } finally {
      setDetecting(false);
    }
  }, [src]);

  // Try once automatically when the dialog opens so staff barely have to adjust.
  useEffect(() => { runDetect(true); }, [runDetect]);

  const resetOriginal = () => {
    setWorkSrc(src); setAspect(ASPECTS[0].value);
    setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
    setDetected(false); setDetectMsg('');
  };

  const confirm = async () => {
    if (!pixels) return;
    const blob = await getCroppedBlob(workSrc, pixels, rotation);
    onConfirm(blob);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-xl" data-testid="id-doc-crop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Crop size={16} className="text-[#1763B6]" />{title}</DialogTitle>
          <DialogDescription className="text-[11px]">Drag to position, scroll or use the slider to zoom, rotate a sideways photo, then save. Crop tightly to the edges of the card so it fills the frame.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="id-doc-crop-aspects">
          {ASPECTS.map((a) => (
            <button key={a.key} onClick={() => setAspect(a.value)}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${Math.abs(aspect - a.value) < 0.001 ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`id-doc-crop-aspect-${a.key}`}>{a.label}</button>
          ))}
          <div className="ml-auto flex gap-1">
            <button onClick={() => runDetect(false)} disabled={detecting}
              className="flex items-center gap-1 rounded-full bg-[#1763B6] px-3 py-1 text-xs font-semibold text-white hover:bg-[#145298] disabled:opacity-60"
              data-testid="id-doc-crop-autodetect" title="Snap the frame to the card edges">
              {detecting ? <Loader2 size={13} className="animate-spin" /> : <ScanLine size={13} />}
              {detecting ? 'Detecting…' : 'Auto-detect edges'}
            </button>
            {detected && (
              <button onClick={resetOriginal} className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200" data-testid="id-doc-crop-reset" title="Undo auto-detect">
                <Undo2 size={13} />Original
              </button>
            )}
            <button onClick={() => setRotation((r) => (r - 90 + 360) % 360)} className="rounded border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50" data-testid="id-doc-crop-rotate-left" title="Rotate left"><RotateCcw size={14} /></button>
            <button onClick={() => setRotation((r) => (r + 90) % 360)} className="rounded border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50" data-testid="id-doc-crop-rotate-right" title="Rotate right"><RotateCw size={14} /></button>
          </div>
        </div>
        {detectMsg && <div className="text-[11px] font-medium text-[#1763B6]" data-testid="id-doc-crop-detect-msg">{detectMsg}</div>}
        <div className="relative mt-1 h-80 w-full overflow-hidden rounded-md bg-slate-900">
          <Cropper image={workSrc} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect} showGrid restrictPosition={false}
            onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={onComplete} />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <ZoomIn size={15} className="text-slate-400" />
          <input type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full" data-testid="id-doc-crop-zoom" />
        </div>
        <DialogFooter>
          <button onClick={onCancel} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50" data-testid="id-doc-crop-cancel">Cancel</button>
          <button onClick={confirm} disabled={busy || !pixels} className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50" data-testid="id-doc-crop-confirm">{busy ? 'Saving…' : 'Save cropped image'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentCropDialog;
